/*
  Component: Collection Sort — keeping the two controls in step.
  Responsibility: when one sort control changes, make the other say the same thing. Nothing
                  else. Which products come back, and refetching them, is Dawn's.

  The collection page offers the same choice twice: a dropdown beside the product grid and
  an accordion at the top of the filter drawer. They are two views of one value, so choosing
  in either has to show in both, and the trigger beside the grid has to say what was chosen.

  WHY THIS FILE HAS TO EXIST. Dawn refetches and redraws the section on every change, and
  the natural assumption — the one an earlier comment in snippets/kl-collection-sort.liquid
  actually made — is that both controls come back freshly rendered and correct. They do not.
  assets/facets.js:215 swaps in only the elements matching `#FacetFiltersForm .js-filter,
  #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter`, and facets.js:307
  refreshes three more by class. Neither control is in either set. Nothing touches them, so
  without this file the dropdown goes on saying "Featured" for the rest of the session.

  Delegated from the section, so it survives everything Dawn does replace and there is
  nothing to rebind. Both controls are inside it.

  `change` rather than `input`, and no debounce. facets.js listens for `input` on the form
  and waits 800ms before refetching; this runs at once, so the control the shopper is
  looking at agrees with them immediately rather than a beat later.

  Assigning .checked fires no event, so writing to the other control cannot come back round
  as another change. That is the whole of why this cannot loop.

  Radios of the same name in different forms are separate groups, which is exactly why the
  two controls can disagree in the first place — and also why the loop below sets every
  input rather than only the chosen one. Setting one radio true unchecks its own group and
  says nothing about the other.

  The whole file is inside a function because a <script src> without type="module" shares
  one scope with every other one on the page, so a top-level `const` here is a global. This
  file and assets/kl-collection-grid-toggle.js both named the section's hook
  COLLECTION_SELECTOR and the second the browser parsed died on "Identifier already been
  declared" — see the longer note in that file, which is where the bug surfaced.
*/

(() => {
  const COLLECTION_SELECTOR = '[data-kl-collection]';
  const SORT_INPUT_SELECTOR = '[data-kl-sort-input]';
  const SORT_CURRENT_SELECTOR = '[data-kl-sort-current]';

  /*
    The chosen state is read straight off the inputs, and nothing else has to be told about
    it: the filled box in the drawer and the bold row in the dropdown are both CSS reading
    :checked. So this sets the value and the stylesheet draws the consequence.
  */
  function applySortValue(collectionElement, sortValue, sortLabel) {
    collectionElement.querySelectorAll(SORT_INPUT_SELECTOR).forEach((inputElement) => {
      inputElement.checked = inputElement.value === sortValue;
    });

    if (!sortLabel) return;

    collectionElement.querySelectorAll(SORT_CURRENT_SELECTOR).forEach((currentElement) => {
      if (currentElement.textContent !== sortLabel) currentElement.textContent = sortLabel;
    });
  }

  function handleSortChange(collectionElement, event) {
    const inputElement = event.target.closest(SORT_INPUT_SELECTOR);

    if (!inputElement) return;

    try {
      applySortValue(collectionElement, inputElement.value, inputElement.dataset.klSortLabel);
    } catch (error) {
      console.debug('kl-collection-sort-sync: could not apply the chosen order', {
        sortValue: inputElement.value,
        error,
      });
    }
  }

  function initSortSync(collectionElement) {
    collectionElement.addEventListener('change', (event) => handleSortChange(collectionElement, event));
  }

  document.querySelectorAll(COLLECTION_SELECTOR).forEach(initSortSync);
})();
